import { auth } from './firebase';
import { readConsent, readPlatformIds } from './consent';

// Same-origin by default: Firebase Hosting rewrites /api/** to the Cloud Run
// service (see firebase.json), so there is no CORS preflight and no base URL to
// configure. REACT_APP_API_BASE exists only for pointing a local dev server at
// a deployed API.
const API_BASE = process.env.REACT_APP_API_BASE || '';

export class ApiError extends Error {
  constructor(status, message, detail) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function bearerToken(forceRefresh) {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;
  return currentUser.getIdToken(forceRefresh);
}

/**
 * Reads the body, reporting whether it was actually JSON.
 *
 * The flag matters because the two callers want opposite things. An error
 * response is often not JSON at all — a Cloud Run 502, an infrastructure error
 * page — and its raw text is the most useful thing to put in the message. A
 * *successful* response that is not JSON is a broken contract, and returning it
 * as a string hands the caller something it will fail on much later.
 */
async function parseBody(response) {
  if (response.status === 204) return { payload: null, isJson: true };
  const text = await response.text();
  if (!text) return { payload: null, isJson: true };
  try { return { payload: JSON.parse(text), isJson: true }; }
  catch (e) { return { payload: text, isJson: false }; }
}

/**
 * Calls the Kinship API with the caller's Firebase ID token attached.
 *
 * Retries once on 401 with a forced token refresh. This is the same staleness
 * problem AuthContext.reloadUser() works around: a user who just verified their
 * email still carries email_verified=false in their cached JWT until the token
 * is refreshed, so a first attempt can fail purely because the token predates
 * the change.
 */
export async function apiFetch(path, { method = 'GET', body, signal } = {}) {
  let lastResponse = null;

  for (const forceRefresh of [false, true]) {
    const token = await bearerToken(forceRefresh);
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    // Relay the advertising-consent decision to the server.
    //
    // It already lives in a cookie on .kinshiptrees.com, which the API could
    // read for itself were it not for Firebase Hosting, which strips every
    // cookie except `__session` before a request reaches Cloud Run. The app
    // talks to the API through a Hosting rewrite, so the cookie cannot arrive
    // and a header is the only channel left.
    //
    // This copies the person's own decision rather than making one. Sending
    // nothing, or sending anything the server does not recognise, counts as a
    // refusal, so the worst a tampered-with header achieves is opting its owner
    // out of measurement they were free to decline anyway.
    const adsConsent = readConsent();
    if (adsConsent) headers['X-Ads-Consent'] = adsConsent;

    // The advertising platform's own identifiers, for the same reason and by
    // the same route. `_fbc` names the advert click that brought somebody here,
    // which is what lets a signup be credited to an advert without the platform
    // having to guess who anybody is from an email address. Hosting strips these
    // cookies exactly as it strips the consent one, so they ride in headers too.
    //
    // readPlatformIds returns nothing at all without consent, so this adds no
    // header for somebody who declined.
    const platformIds = readPlatformIds();
    if (platformIds._fbc) headers['X-Ads-Click-Id'] = platformIds._fbc;
    if (platformIds._fbp) headers['X-Ads-Browser-Id'] = platformIds._fbp;
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      signal,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    // Only a 401 is worth retrying, and only while we still have an unused
    // forced refresh — anything else is a real answer.
    if (response.status !== 401 || forceRefresh) {
      lastResponse = response;
      break;
    }
    lastResponse = response;
  }

  const { payload, isJson } = await parseBody(lastResponse);

  if (!lastResponse.ok) {
    const detail = payload && typeof payload === 'object' ? payload.detail : payload;
    throw new ApiError(
      lastResponse.status,
      detail || `Request failed with status ${lastResponse.status}`,
      payload,
    );
  }

  // A 2xx that isn't JSON has to fail here, at the boundary. It used to be
  // returned as a raw string, which meant the caller got something shaped
  // nothing like the object it expected and blew up much later, somewhere
  // unrelated — a snapshot load reporting "cannot read properties of undefined"
  // deep in a merge, rather than "the API returned HTML".
  //
  // The case that produced this is worth naming, because it is the one a
  // developer will hit again: on a dev server with no /api proxy, every API
  // call falls through to the SPA's catch-all and returns index.html with a
  // 200. Nothing about that looks like a failure until it is far too late.
  if (!isJson) {
    const contentType = lastResponse.headers.get('content-type') || 'no content-type';
    throw new ApiError(
      lastResponse.status,
      `Expected JSON from ${path} but got ${contentType}. ` +
      `If this is a dev server, /api is probably not proxied to the API.`,
      payload,
    );
  }

  return payload;
}

export const apiGet  = (path, opts)       => apiFetch(path, { ...opts, method: 'GET' });
export const apiPost  = (path, body, opts) => apiFetch(path, { ...opts, method: 'POST', body });
export const apiPatch = (path, body, opts) => apiFetch(path, { ...opts, method: 'PATCH', body });
export const apiPut   = (path, body, opts) => apiFetch(path, { ...opts, method: 'PUT', body });
export const apiDelete = (path, opts)      => apiFetch(path, { ...opts, method: 'DELETE' });

/**
 * The caller's effective permissions on a tree.
 *
 * Not yet wired into TreeContext — Phase 0 is additive. This is what will
 * replace the client-side editablePersonIds computation in Phase 1.
 */
export const fetchTreeAccess = (treeId) => apiGet(`/api/trees/${encodeURIComponent(treeId)}/access`);

/**
 * What an invite link points at, for a visitor who has not signed in yet.
 *
 * The only unauthenticated call the client makes — apiFetch simply omits the
 * Authorization header when there is no user, which is exactly what this wants.
 *
 * POST with the token in the body rather than in a path, so it stays out of the
 * server's request logs and the browser's address bar.
 */
export const lookupInvite = (token) => apiPost('/api/invites/lookup', { token });

/** Redeems an invite link for the signed-in caller. */
export const acceptInviteToken = (token) => apiPost('/api/invites/accept', { token });

/**
 * What deleting this account would destroy, without destroying it.
 *
 * The confirmation dialog is the only safeguard on an irreversible action, so
 * it names real trees and real counts rather than "your data" — which means
 * asking the server what is actually there.
 */
/**
 * Asks the server to report a paid subscription to the advertising platform.
 *
 * The browser asks because the request carries the consent header, and a
 * Braintree webhook does not. The server verifies the subscription for itself
 * before reporting anything, so this is a prompt rather than a claim, and it is
 * idempotent, so calling it twice is the intended usage rather than a mistake.
 *
 * Never allowed to fail a flow it is attached to. Somebody who has just paid
 * has paid, and an advertising platform having a slow afternoon is not their
 * problem.
 */
export const reportSubscriptionConversion = () =>
  apiPost('/api/conversions/subscription').catch(() => {});

export const previewAccountDeletion = () => apiDelete('/api/account?dryRun=true');

/** Deletes the caller's own account. There is no going back from this one. */
export const deleteAccount = () => apiDelete('/api/account');
